import { useCallback, useEffect, useRef } from 'react';

interface UsePilotRecoveryStateOptions {
  heartbeatTimeoutMs: number;
  onHeartbeatTimeout: () => void;
}

export function usePilotRecoveryState({
  heartbeatTimeoutMs,
  onHeartbeatTimeout,
}: UsePilotRecoveryStateOptions) {
  const recoveryRequestedRef = useRef(false);
  const lastHeartbeatAtRef = useRef(0);
  const heartbeatWatchdogRef = useRef<ReturnType<typeof setInterval> | null>(
    null,
  );
  const recoveryPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const setRecoveryRequested = useCallback((nextValue: boolean) => {
    recoveryRequestedRef.current = nextValue;
  }, []);

  const stopHeartbeatWatchdog = useCallback(() => {
    if (heartbeatWatchdogRef.current) {
      clearInterval(heartbeatWatchdogRef.current);
      heartbeatWatchdogRef.current = null;
    }
    lastHeartbeatAtRef.current = 0;
  }, []);

  const startHeartbeatWatchdog = useCallback(() => {
    stopHeartbeatWatchdog();
    recoveryRequestedRef.current = false;
    lastHeartbeatAtRef.current = Date.now();
    heartbeatWatchdogRef.current = setInterval(() => {
      if (
        lastHeartbeatAtRef.current > 0 &&
        Date.now() - lastHeartbeatAtRef.current >= heartbeatTimeoutMs &&
        !recoveryRequestedRef.current
      ) {
        recoveryRequestedRef.current = true;
        onHeartbeatTimeout();
      }
    }, 1_000);
  }, [heartbeatTimeoutMs, onHeartbeatTimeout, stopHeartbeatWatchdog]);

  const onHeartbeat = useCallback(() => {
    lastHeartbeatAtRef.current = Date.now();
  }, []);

  const stopRecoveryPolling = useCallback(() => {
    if (recoveryPollRef.current) {
      clearInterval(recoveryPollRef.current);
      recoveryPollRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      stopHeartbeatWatchdog();
      stopRecoveryPolling();
    };
  }, [stopHeartbeatWatchdog, stopRecoveryPolling]);

  return {
    recoveryRequestedRef,
    recoveryPollRef,
    heartbeatWatchdogRef,
    lastHeartbeatAtRef,
    setRecoveryRequested,
    startHeartbeatWatchdog,
    stopHeartbeatWatchdog,
    onHeartbeat,
    stopRecoveryPolling,
  };
}
